/**
 * Team competition utility functions
 *
 * Helper functions for working with team competition data, including
 * competitor sorting and filtering, session results, and team information.
 */

import type { TeamCompetitorMemberSummary, TeamSessionSummary } from '../types/api.types';
import { TEAM_COLORS } from '../constants/teamColors';

/**
 * Sort team competitors by points scored in descending order
 *
 * @param competitors - Array of team competitors
 * @returns Sorted array (highest points first)
 */
export const sortTeamCompetitorsByPoints = (
  competitors: TeamCompetitorMemberSummary[]
): TeamCompetitorMemberSummary[] => {
  return [...competitors].sort((a, b) => {
    const aPoints = Number(a.pointsScored || 0);
    const bPoints = Number(b.pointsScored || 0);
    return bPoints - aPoints;
  });
};

/**
 * Filter competitors by team and sort by points
 *
 * @param results - Array of all team competitors
 * @param team - Team to filter by ('USA' or 'EUR')
 * @returns Filtered and sorted array of competitors
 */
export const getTeamCompetitors = (
  results: TeamCompetitorMemberSummary[],
  team: 'USA' | 'EUR'
): TeamCompetitorMemberSummary[] => {
  const filtered = results.filter((c) => c.team === team);
  return sortTeamCompetitorsByPoints(filtered);
};

/**
 * Parse points value from string or number
 *
 * @param points - Points value (can be string, number, undefined, or null)
 * @returns Numeric points value (0 if undefined/null)
 */
export const parsePoints = (points: string | number | undefined | null): number => {
  if (points === undefined || points === null) return 0;
  return typeof points === 'number' ? points : parseFloat(points);
};

/**
 * Get CSS class for team badge
 *
 * @param teamName - Team name ('USA' or 'EUR')
 * @returns CSS class name for the team
 */
export const getTeamClass = (teamName: string): string => {
  return teamName === 'USA' ? 'team-usa' : 'team-eur';
};

/**
 * Get display label for team badge
 *
 * @param teamName - Team name
 * @param winner - Winning team name
 * @returns Display label (adds "Win" suffix for winner)
 */
export const getTeamLabel = (teamName: string, winner: string): string => {
  return teamName === winner ? `${winner} Win` : teamName;
};

/**
 * Get label style based on win status
 *
 * @param teamName - Team name
 * @param winner - Winning team name
 * @returns Style object with fontSize
 */
export const getLabelStyle = (teamName: string, winner: string): { fontSize: 'large' | 'small' } => {
  return { fontSize: teamName === winner ? 'large' : 'small' };
};

/**
 * Get heading style with team color
 *
 * @param teamName - Team name ('USA' or 'EUR')
 * @returns React CSSProperties object with color and alignment
 */
export const getHeadingStyle = (teamName: string): React.CSSProperties => {
  const color = teamName === 'USA' ? TEAM_COLORS.USA.hex : TEAM_COLORS.EUR.hex;
  return { textAlign: 'center', color };
};

/**
 * Team information for display
 */
export interface TeamInfo {
  name: string;
  label: string;
  class: string;
  labelStyle: { fontSize: 'large' | 'small' };
  headingStyle: React.CSSProperties;
}

/**
 * Get complete team information for display
 *
 * @param teamName - Team name
 * @param winner - Winning team name
 * @returns Team information object
 */
export const getTeamInfo = (teamName: string, winner: string): TeamInfo => ({
  name: teamName,
  label: getTeamLabel(teamName, winner),
  class: getTeamClass(teamName),
  labelStyle: getLabelStyle(teamName, winner),
  headingStyle: getHeadingStyle(teamName),
});

/**
 * Calculate total points for both teams from session results
 *
 * @param sessions - Array of team session summaries
 * @returns Object with teamAPoints and teamBPoints totals
 */
export const calculateSessionTotals = (
  sessions: TeamSessionSummary[]
): { teamAPoints: number; teamBPoints: number } => {
  return sessions.reduce(
    (accumulator, currValue) => ({
      teamAPoints: accumulator.teamAPoints + parsePoints(currValue.teamAPoints),
      teamBPoints: accumulator.teamBPoints + parsePoints(currValue.teamBPoints),
    }),
    { teamAPoints: 0, teamBPoints: 0 }
  );
};
